import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    ButtonGroup,
    Textarea,
    Text,
    HStack,
    Icon,
    FormControl,
    FormLabel,
    useToast,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { TbSpeakerphone } from 'react-icons/tb';
import { useCreateAnnouncement } from '../../hooks/useCreateAnnouncement';

interface SendAnnouncementModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const MAX_LENGTH = 2000;

/** Presets for how long the banner runs before it auto-expires (and is auto-deleted). */
const DURATION_OPTIONS: { label: string; days: number }[] = [
    { label: '1 day', days: 1 },
    { label: '3 days', days: 3 },
    { label: '1 week', days: 7 },
    { label: '2 weeks', days: 14 },
    { label: '30 days', days: 30 },
];
const DEFAULT_DURATION_DAYS = 1;

const SendAnnouncementModal = ({ isOpen, onClose }: SendAnnouncementModalProps) => {
    const [message, setMessage] = useState('');
    const [durationDays, setDurationDays] = useState(DEFAULT_DURATION_DAYS);
    const createAnnouncement = useCreateAnnouncement();
    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            setMessage('');
            setDurationDays(DEFAULT_DURATION_DAYS);
        }
    }, [isOpen]);

    const trimmed = message.trim();
    const isTooLong = message.length > MAX_LENGTH;

    const handleSend = async () => {
        if (!trimmed || isTooLong) return;
        try {
            await createAnnouncement.mutateAsync({ message: trimmed, durationDays });
            const durationLabel = DURATION_OPTIONS.find((o) => o.days === durationDays)?.label ?? `${durationDays} days`;
            toast({
                title: 'Announcement sent',
                description: `Everyone in your organization will be notified. Runs for ${durationLabel}.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
        } catch {
            toast({
                title: 'Failed to send announcement',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader>
                    <HStack spacing={2}>
                        <Icon as={TbSpeakerphone as React.ElementType} w={5} h={5} color="#0071E3" />
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                            Send Announcement
                        </Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Textarea
                        placeholder="Write your announcement..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        minH="140px"
                        fontSize="sm"
                        resize="vertical"
                        autoFocus
                        isDisabled={createAnnouncement.isPending}
                        focusBorderColor="#0071E3"
                    />
                    <HStack justify="space-between" align="center" mt={1}>
                        <Text fontSize="xs" color="gray.400">
                            *this is sent to everyone in your organization
                        </Text>
                        <Text fontSize="xs" color={isTooLong ? 'red.500' : 'gray.400'}>
                            {message.length}/{MAX_LENGTH}
                        </Text>
                    </HStack>
                    <FormControl mt={4}>
                        <FormLabel fontSize="sm" color="gray.700" mb={2}>
                            Run for
                        </FormLabel>
                        <ButtonGroup isAttached variant="outline" size="sm" width="100%" isDisabled={createAnnouncement.isPending}>
                            {DURATION_OPTIONS.map((option) => {
                                const isSelected = option.days === durationDays;
                                return (
                                    <Button
                                        key={option.days}
                                        flex={1}
                                        onClick={() => setDurationDays(option.days)}
                                        bg={isSelected ? '#0071E3' : 'white'}
                                        color={isSelected ? 'white' : 'gray.700'}
                                        borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                        _hover={{ bg: isSelected ? '#0071E3' : 'gray.50' }}
                                        fontWeight={isSelected ? 'semibold' : 'normal'}
                                    >
                                        {option.label}
                                    </Button>
                                );
                            })}
                        </ButtonGroup>
                    </FormControl>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                    <Button size="action" variant="outlineGray" onClick={onClose} isDisabled={createAnnouncement.isPending}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#005BC4' }}
                        leftIcon={<Icon as={TbSpeakerphone as React.ElementType} />}
                        onClick={handleSend}
                        isLoading={createAnnouncement.isPending}
                        isDisabled={!trimmed || isTooLong}
                    >
                        Send to organization
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SendAnnouncementModal;
